/** @module values */

/**
 * A size of something.
 *
 * @typedef {Object} Size
 * @property {number} value
 * @property {string} units
 */

/**
 * Size constructor.
 *
 * @function
 * @param {number} value
 * @param {string} units
 * @return {Size}
 */
export const Size = (value, units) => ({ value, units });

/**
 * Get value.
 *
 * @function
 * @param {Size} size
 * @return {number}
 */
export const getSizeValue = size => size.value;

/**
 * Get units.
 *
 * @function
 * @param {Size} size
 * @return {string}
 */
export const getSizeUnits = size => size.units;

/**
 * Check two sizes for equality.
 *
 * @function
 * @param {Size} sizeA
 * @param {Size} sizeB
 * @return {boolean}
 */
export const isSizeEqual = (sizeA, sizeB) =>
  sizeA.value === sizeB.value && sizeA.units === sizeB.units;

/**
 * A price with value and currency.
 *
 * @typedef {Object} Price
 * @property {number} value
 * @property {string} currency
 */

const assertPricesComparable = (priceA, priceB) => {
  if (priceA.currency !== priceB.currency) {
    throw Error(
      `Cant compare prices with different currencies ${priceA.currency} and ${priceB.currency}`
    );
  }
};

const isPrice = price =>
  price && typeof price.value === 'number' && typeof price.currency === 'string';

/**
 * Price constructor.
 *
 * @function
 * @param {number|Price} value
 * @param {string} currency
 * @return {Price}
 */
export const Price = (value, currency) => {
  if (isPrice(value)) {
    return Price(value.value, value.currency);
  }

  return { value, currency };
};

/**
 * Compare two prices.
 *
 * @function
 * @param {Price} priceA
 * @param {Price} priceB
 * @return {boolean}
 */
export const isPriceLess = (priceA, priceB) => {
  assertPricesComparable(priceA, priceB);

  return priceA.value < priceB.value;
};

/**
 * Check if price value equal zero.
 *
 * @param {Price} price
 * @return {boolean}
 */
export const isPriceZero = price => price.value === 0;

/**
 * Check for two prices equality.
 *
 * @function
 * @param {Price} priceA
 * @param {Price} priceB
 * @return {boolean}
 */
export const isPriceEqual = (priceA, priceB) => {
  return priceA.currency === priceB.currency && priceA.value === priceB.value;
};

/**
 * Get value.
 *
 * @function
 * @param {Price} price
 * @return {number}
 */
export const getPriceValue = price => (price ? price.value : null);

/**
 * Get currency.
 *
 * @function
 * @param {Price} price
 * @return {string}
 */
export const getPriceCurrency = price => (price ? price.currency : null);

/**
 * Add two prices.
 *
 * @function
 * @param {Price} priceA
 * @param {Price} priceB
 * @return {Price}
 */
export const addPrices = (priceA, priceB) => {
  const sum = getPriceValue(priceA) + getPriceValue(priceB);
  const currencyA = getPriceCurrency(priceA);

  assertPricesComparable(priceA, priceB);

  return Price(sum, currencyA);
};

/**
 * Subtract two prices.
 *
 * @function
 * @param {Price} priceA
 * @param {Price} priceB
 * @return {Price}
 */
export const subtractPrices = (priceA, priceB) => {
  assertPricesComparable(priceA, priceB);

  return Price(priceA.value - priceB.value, priceA.currency);
};

/**
 * Multiply price by number.
 *
 * @function
 * @param {Price} price
 * @param {number} multiplier
 * @return {Price}
 */
export const multiplyPrice = (price, multiplier) => {
  if (isPrice(multiplier)) {
    assertPricesComparable(price, multiplier);

    return Price(price.value * multiplier.value, price.currency);
  }

  return Price(price.value * multiplier, price.currency);
};

/**
 * Divide price by number.
 *
 * @function
 * @param {Price} price
 * @param {number} divider
 * @return {Price}
 */
export const dividePrice = (price, divider) => {
  if (isPrice(divider)) {
    assertPricesComparable(price, divider);

    return Price(price.value / divider.value, price.currency);
  }

  return Price(price.value / divider, price.currency);
};
